import { forwardRef, type ReactNode } from "react";

import {
  type ImperativePanelHandle,
  Panel,
  PanelGroup,
  type PanelGroupProps,
  type PanelProps,
  PanelResizeHandle,
  type PanelResizeHandleProps,
} from "react-resizable-panels";

import { cn } from "@btnet/utilities-core";

import "./BuiSplitView.less";

/**
 * BuiSplitView props - extends react-resizable-panels PanelGroupProps with testid.
 * Provides full access to the library's PanelGroup API with Blueprint styling.
 *
 * `data-testid` is omitted because `testid` maps to it — accepting both would cause conflicts.
 *
 * Note: `children` is explicitly included due to React 18 type changes
 * (the library's types were built for React 17 where FC implicitly included children).
 */
export interface IBuiSplitViewProps
  extends Omit<PanelGroupProps, "data-testid"> {
  testid: string;
  children?: ReactNode;
}

/**
 * BuiSplitView.Panel props - extends react-resizable-panels PanelProps with testid.
 * Provides full access to the library's Panel API with Blueprint styling.
 *
 * Note: `children` is explicitly included due to React 18 type changes.
 */
export interface IBuiSplitViewPanelProps
  extends Omit<PanelProps, "data-testid"> {
  testid: string;
  children?: ReactNode;
}

/**
 * BuiSplitView.Handle props - extends react-resizable-panels PanelResizeHandleProps with testid.
 * Provides full access to the library's PanelResizeHandle API with Blueprint styling.
 *
 * Note: `children` is explicitly included due to React 18 type changes.
 */
export interface IBuiSplitViewHandleProps
  extends Omit<PanelResizeHandleProps, "data-testid"> {
  testid: string;
  children?: ReactNode;
}

const BuiSplitViewComponent = ({
  testid,
  className,
  direction = "horizontal",
  ...props
}: IBuiSplitViewProps) => (
  <PanelGroup
    data-testid={testid}
    className={cn("bui-splitview", className)}
    direction={direction}
    {...props}
  />
);

const PanelComponent = forwardRef<
  ImperativePanelHandle,
  IBuiSplitViewPanelProps
>(function BuiSplitViewPanel({ testid, className, ...props }, ref) {
  return (
    <Panel
      ref={ref}
      data-testid={testid}
      className={cn("bui-splitview-panel", className)}
      {...props}
    />
  );
});

const HandleComponent = ({
  testid,
  className,
  ...props
}: IBuiSplitViewHandleProps) => (
  <PanelResizeHandle
    data-testid={testid}
    className={cn("bui-splitview-handle", className)}
    {...props}
  />
);

/**
 * BuiSplitView - a styled wrapper around react-resizable-panels PanelGroup.
 * Provides full access to the library's API with Blueprint styling.
 * Direct children must be BuiSplitView.Panel and BuiSplitView.Handle components.
 * @param props - The component props.
 * @param props.testid - Test ID for the split view group.
 * @param props.className - Additional CSS class names.
 * @param props.direction - Resize direction ("horizontal" or "vertical"). Defaults to "horizontal".
 */
export const BuiSplitView = Object.assign(BuiSplitViewComponent, {
  /**
   * BuiSplitView.Panel - a styled wrapper around react-resizable-panels Panel.
   * Supports size constraints (minSize, maxSize, defaultSize), collapsible behavior, and landmark roles.
   * Sizes are percentages (0-100). The Panel renders as a single div element.
   * Forwards ref to expose ImperativePanelHandle (collapse, expand, resize, getSize).
   * @param props - The component props.
   * @param props.testid - Test ID for the panel.
   * @param props.className - Additional CSS class names.
   */
  Panel: PanelComponent,
  /**
   * BuiSplitView.Handle - a styled wrapper around react-resizable-panels PanelResizeHandle.
   * Renders as an accessible separator with keyboard resize support (Arrow keys, Home, End).
   * @param props - The component props.
   * @param props.testid - Test ID for the resize handle.
   * @param props.className - Additional CSS class names.
   */
  Handle: HandleComponent,
});
